import { Link } from "react-router-dom";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { useAuth } from "../../lib/auth";
import { Car, Bike, Sparkles, MapPin, Wallet, Clock, ShieldCheck, ArrowRight, Briefcase } from "lucide-react";

export default function RideLandingPage() {
  const { user } = useAuth();

  return (
    <div className="min-h-[80vh]" data-testid="ride-landing">
      {/* Hero */}
      <section className="relative bg-brand-ink text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-gradient-to-br from-brand-gold/40 via-transparent to-brand-terracotta/30" />
        </div>
        <div className="relative max-w-6xl mx-auto px-5 sm:px-8 py-12 sm:py-20">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-gold/20 text-brand-gold text-xs uppercase tracking-[0.22em] font-bold mb-5">
            <Sparkles className="w-3.5 h-3.5" />
            Phase 1 · N'Djamena
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight mb-4">
            Kanama Ride
          </h1>
          <p className="text-lg sm:text-xl text-white/85 max-w-2xl mb-8 leading-relaxed">
            Réservez un <strong className="text-brand-gold">taxi</strong> ou un{" "}
            <strong className="text-brand-gold">moto-taxi</strong> à N'Djamena en quelques secondes.
            Tarifs transparents, paiement en espèces, courses tracées sur la carte.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl mb-10">
            <Link to="/ride/request?type=taxi" data-testid="ride-cta-taxi">
              <Button size="lg" className="w-full h-14 rounded-2xl bg-brand-gold hover:bg-brand-goldDark text-brand-black font-semibold text-base shadow-lg">
                <Car className="w-5 h-5 mr-2" strokeWidth={2.2} />
                Commander un taxi
              </Button>
            </Link>
            <Link to="/ride/request?type=moto" data-testid="ride-cta-moto">
              <Button size="lg" className="w-full h-14 rounded-2xl bg-brand-terracotta hover:bg-brand-terracottaHover text-white font-semibold text-base shadow-lg">
                <Bike className="w-5 h-5 mr-2" strokeWidth={2.2} />
                Commander un moto-taxi
              </Button>
            </Link>
          </div>

          {/* Driver CTA */}
          <Card className="bg-white/10 backdrop-blur-sm border-white/15 p-5 rounded-2xl flex items-center justify-between gap-4 flex-wrap max-w-xl">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-brand-gold/20 flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-brand-gold" />
              </div>
              <div>
                <div className="font-heading font-semibold text-white">Devenez chauffeur Kanama</div>
                <div className="text-xs text-white/70">Inscription en quelques minutes</div>
              </div>
            </div>
            <Link to={user && user !== false && user.role === "driver" ? "/ride/driver" : "/ride/driver/signup"} data-testid="ride-cta-driver">
              <Button variant="outline" className="rounded-full border-brand-gold/50 text-brand-gold hover:bg-brand-gold/10">
                {user && user !== false && user.role === "driver" ? "Mon espace chauffeur" : "S'inscrire"}
                <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </Link>
          </Card>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { icon: MapPin, title: "Position en direct", desc: "Suivez votre chauffeur sur la carte jusqu'à l'arrivée." },
            { icon: Wallet, title: "Paiement transparent", desc: "Prix annoncé avant la course · paiement en espèces." },
            { icon: ShieldCheck, title: "Chauffeurs vérifiés", desc: "CNI + permis contrôlés par l'équipe Kanama." },
            { icon: Clock, title: "Estimation rapide", desc: "Distance, durée et tarif calculés en 1 clic." },
            { icon: Car, title: "Taxi 4 places", desc: "Voiture climatisée pour le confort." },
            { icon: Bike, title: "Moto-taxi rapide", desc: "Idéal en heure de pointe à N'Djamena." },
          ].map((f, i) => (
            <Card key={i} className="p-5 rounded-2xl border-brand-line">
              <div className="w-10 h-10 rounded-xl bg-brand-sandlight flex items-center justify-center text-brand-gold mb-3">
                <f.icon className="w-5 h-5" />
              </div>
              <h3 className="font-heading font-semibold text-brand-ink mb-1">{f.title}</h3>
              <p className="text-sm text-brand-inkSoft leading-relaxed">{f.desc}</p>
            </Card>
          ))}
        </div>
      </section>
    </div>
  );
}
